"use client";

import React, { useRef } from "react";
import { testimonials } from "./testimonialsData";
import Image from "next/image";
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useTransform,
} from "framer-motion";

// Keep a value within [min, max), wrapping around for a seamless loop.
const wrap = (min, max, v) => {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
};

const Testimonial = () => {
  const baseX = useMotionValue(0);
  const isPausedRef = useRef(false);

  // The track holds two copies of the list, so translating by -50% of its
  // width brings us back to the start — wrap keeps x within that range.
  const x = useTransform(baseX, (v) => `${wrap(-50, 0, v)}%`);

  useAnimationFrame((t, delta) => {
    if (isPausedRef.current) return;
    // ~5% of the track width per second.
    baseX.set(baseX.get() - (delta / 1000) * 1);
  });

  const loopedTestimonials = [...testimonials, ...testimonials];

  return (
    <section className="bg-white pt-16 px-5 sm:px-6 lg:px-20 overflow-hidden">
      <div className="flex flex-col items-center gap-6 mb-6 lg:mb-16">
        <div className="px-2 py-1 lg:py-1.5 inline-flex items-center gap-1 lg:gap-2 rounded-full  font-kalam border border-zinc-200 text-[#292929] text-[12px] italic font-bold">
          <span className="inline-flex items-center justify-center bg-[#7F7FFF]/20 rounded-full w-2 h-2 lg:w-3.5 lg:h-3.5">
            <span className="block w-1 h-1 lg:w-1.5 lg:h-1.5 rounded-full bg-[#7F7FFF]" />
          </span>
          <div className=" h-3 text-center justify-start text-zinc-600 text-[10px] lg:text-xs font-bold font-['Kalam'] tracking-widest lg:leading-4 lg:tracking-wide">
            Testimonials
          </div>
        </div>

        <h2 className="text-[24px] sm:text-[30px] lg:text-5xl font-medium  md:text-[44px] tracking-[-0.5px] leading-[1.15]  lg:leading-[1.15]  3xl:text-[64px] 4xl:text-[80px]  text-[#242424] text-center">
          Loved by teams scaling
          <br />
          creative performance.
        </h2>
      </div>

      <div
        className="-mx-5 sm:-mx-6 lg:-mx-20 overflow-hidden"
        onMouseEnter={() => (isPausedRef.current = true)}
        onMouseLeave={() => (isPausedRef.current = false)}
      >
        <motion.div
          style={{ x }}
          className="flex items-stretch gap-3 w-max px-5 sm:px-6 lg:px-10 py-8 lg:py-16"
        >
          {loopedTestimonials.map((t, i) => (
            <div
              key={`${t.id}-${i}`}
              className={`${t.rotate} peer peer-hover:translate-x-6 shadow-sm cursor-pointer group bg-gradient-to-b hover:from-[#000000]  hover:to-[#050838] flex-shrink-0 w-[280px] lg:w-[280px] lg:h-[280px] p-6 lg:p-7 bg-[#F4F4F6] rounded-2xl  outline outline-4 hover:outline-offset-0  outline-white flex flex-col hover:outline-none justify-between items-start gap-6 transition-all duration-500 ease-out relative hover:rotate-0 hover:scale-105 hover:z-50 `}
            >
              <div className="self-stretch flex flex-col justify-between items-start gap-6 lg:gap-0 lg:h-72">
                <div className="self-stretch justify-start group-hover:text-white text-slate-900 text-[13px] lg:text-sm font-normal font-['Inter'] leading-[1.5]">
                  {t.comment}
                </div>
                <div className="size- inline-flex justify-start items-center gap-2">
                  <div className=" border-2 border-white rounded-full ">
                    <Image
                      src={t.image}
                      alt="image"
                      width={48}
                      height={48}
                      className="w-10 h-10 rounded-full"
                    />
                  </div>

                  <div className="size- inline-flex flex-col justify-start items-start gap-[2.81px]">
                    <div className="justify-start group-hover:text-white  text-slate-900 text-xs font-medium font-['Inter'] leading-4">
                      {t.name}
                    </div>
                    <div className="justify-start group-hover:text-white text-slate-600 text-[9.84px] font-normal font-['Inter'] leading-4">
                      {t.role}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonial;
